import React, { useState } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { useDMOps } from "../../../hooks/useDMOps";
import { DMStatus } from "../../../types";

const channelIcon: Record<"email" | "linkedin" | "note", string> = {
  email: "✉",
  linkedin: "in",
  note: "📝",
};

const channelLabel: Record<"email" | "linkedin" | "note", string> = {
  email: "Email",
  linkedin: "LinkedIn",
  note: "Private note",
};

const statusColor: Record<DMStatus, string> = {
  draft: "bg-yellow-50 text-yellow-600",
  in_review: "bg-blue-50 text-blue-600",
  approved: "bg-green-50 text-green-600",
  scheduled: "bg-purple-50 text-purple-600",
  sent: "bg-gray-50 text-gray-500",
  archived: "bg-gray-50 text-gray-400",
};

type Props = {
  dmId: string;
};

export const DMCard: React.FC<Props> = ({ dmId }) => {
  const { state, patchState } = useMonyawn();
  const { setDMStatus } = useDMOps();
  const dm = state.dms.find((d) => d.dm_id === dmId);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");

  if (!dm) return null;

  function startEdit() {
    setEditSubject(dm!.subject);
    setEditBody(dm!.body);
    setEditing(true);
    setExpanded(true);
  }

  function saveEdit() {
    patchState(
      (current) => ({
        ...current,
        dms: current.dms.map((d) =>
          d.dm_id === dmId
            ? { ...d, subject: editSubject, body: editBody, updated_at: new Date().toISOString() }
            : d,
        ),
      }),
      "DM updated.",
    );
    setEditing(false);
  }

  const preview = dm.body.slice(0, 120) + (dm.body.length > 120 ? "…" : "");

  return (
    <div className="rounded-2xl border border-black/8 bg-white shadow-sm hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="w-8 h-8 rounded-xl bg-brand-surface border border-black/8 flex items-center justify-center text-xs font-bold text-brand-muted flex-none">
          {channelIcon[dm.channel]}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-ink truncate">
            {dm.subject || `${channelLabel[dm.channel]} draft`}
          </p>
          <p className="text-xs text-brand-muted">{channelLabel[dm.channel]}</p>
        </div>
        <span
          className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${statusColor[dm.status]}`}
        >
          {dm.status}
        </span>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-brand-muted hover:text-brand-ink transition-colors ml-1"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path
              d={expanded ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"}
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Preview line when collapsed */}
      {!expanded && dm.body && (
        <p className="px-4 pb-3 text-xs text-brand-muted leading-relaxed truncate">{preview}</p>
      )}

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-black/5 pt-3 space-y-3">
          {editing ? (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Subject
                </span>
                <input
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Message
                </span>
                <textarea
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
                  rows={6}
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  className="bg-brand-accent text-white px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-brand-accent-strong transition-all"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="bg-brand-surface border border-black/10 text-brand-ink px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-black/5 transition-all"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-brand-ink leading-relaxed whitespace-pre-wrap">
                {dm.body}
              </p>
              {dm.extracted_signals && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {dm.extracted_signals.emails.map((e) => (
                    <span
                      key={e}
                      className="bg-blue-50 text-blue-600 text-xs px-2 py-0.5 rounded-full font-medium"
                    >
                      {e}
                    </span>
                  ))}
                  {dm.extracted_signals.dates.map((d) => (
                    <span
                      key={d}
                      className="bg-green-50 text-green-600 text-xs px-2 py-0.5 rounded-full font-medium"
                    >
                      {d}
                    </span>
                  ))}
                  {dm.extracted_signals.names.map((n) => (
                    <span
                      key={n}
                      className="bg-purple-50 text-purple-600 text-xs px-2 py-0.5 rounded-full font-medium"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 flex-wrap pt-1">
                <button
                  onClick={startEdit}
                  className="bg-brand-surface border border-black/10 text-brand-ink px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-black/5 transition-all"
                >
                  Edit
                </button>
                {dm.status === "draft" && (
                  <button
                    onClick={() => setDMStatus(dmId, "approved")}
                    className="bg-green-50 text-green-600 border border-green-100 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-green-100 transition-all"
                  >
                    Approve
                  </button>
                )}
                {(dm.status === "draft" || dm.status === "approved") && (
                  <button
                    onClick={() => setDMStatus(dmId, "sent")}
                    className="bg-blue-50 text-blue-600 border border-blue-100 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-blue-100 transition-all"
                  >
                    Mark sent
                  </button>
                )}
                {dm.status !== "archived" && (
                  <button
                    onClick={() => setDMStatus(dmId, "archived")}
                    className="text-brand-muted px-3 py-1.5 rounded-xl text-xs hover:bg-black/5 transition-all"
                  >
                    Archive
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
